import type {
  InsightListData,
  InsightResult,
  JourneyTimelineData,
  NarrativeBlockData,
  NumberedInsightListData,
  TileMeta,
} from '../types';

type LooseInsight = {
  renderer?: string;
  display_name?: string;
  title?: string;
  subtitle?: string;
  body?: string;
  headline?: string;
  description?: string;
  overline?: string;
  footer?: string;
  customerName?: string;
  summaryStats?: JourneyTimelineData['summaryStats'];
  milestones?: JourneyTimelineData['milestones'];
  steps?: Array<{
    date: string;
    label: string;
    detail?: string;
    isHighlight?: boolean;
  }>;
  items?: Array<{
    title?: string;
    summary?: string;
    detail?: string;
    description?: string;
    affectsLabel?: string;
    category?: string;
    iconVariant?: 'warning' | 'clock' | 'trend';
  }>;
  meta?: TileMeta;
};

const withMeta = <T extends object>(result: T, meta?: TileMeta): T =>
  meta ? { ...result, meta } : result;

/**
 * Map backend insight payloads (UI_PAGE_ASSEMBLY / InsightResult.interface)
 * into the shapes our renderers already consume. Also accepts existing UI mock shapes.
 */
export const adaptInsightResult = (raw: unknown): InsightResult => {
  const data = raw as LooseInsight;
  const renderer = data.renderer;
  const meta = data.meta;

  if (
    renderer === 'journeyTimeline' ||
    (Array.isArray(data.steps) && data.steps.length > 0) ||
    (Array.isArray(data.milestones) && data.milestones.length > 0 && data.summaryStats)
  ) {
    const milestones =
      data.milestones ??
      (data.steps ?? []).map(step => ({
        date: step.date,
        title: step.label,
        detail: step.detail,
        isHighlight: step.isHighlight,
      }));

    return withMeta(
      {
        display_name: data.display_name ?? data.title,
        customerName: data.customerName,
        summaryStats: data.summaryStats ?? [],
        milestones,
        footer: data.footer,
      } as JourneyTimelineData,
      meta,
    );
  }

  if (renderer === 'numberedInsightList') {
    return withMeta(
      {
        display_name: data.display_name ?? data.title,
        items: (data.items ?? []).map(item => ({
          summary: item.summary ?? item.title ?? '',
          detail: item.detail ?? '',
        })),
      } as NumberedInsightListData,
      meta,
    );
  }

  if (renderer === 'insightList') {
    return withMeta(
      {
        display_name: data.display_name ?? data.title,
        items: (data.items ?? []).map(item => ({
          title: item.title ?? '',
          description: item.description ?? item.detail ?? '',
          affectsLabel: item.affectsLabel,
          category: item.category,
          iconVariant: item.iconVariant,
        })),
      } as InsightListData,
      meta,
    );
  }

  if (renderer === 'narrativeBlock' || data.body != null) {
    return withMeta(
      {
        display_name: data.display_name ?? data.title,
        overline: data.overline,
        headline: data.headline ?? data.title ?? '',
        description: data.description ?? data.body,
      } as NarrativeBlockData,
      meta,
    );
  }

  // UI mock shapes (no renderer discriminant)
  if (data.summaryStats && data.milestones) {
    return data as JourneyTimelineData;
  }

  if (Array.isArray(data.items) && data.items.length > 0) {
    const first = data.items[0];
    if (first.summary != null) {
      return data as NumberedInsightListData;
    }
    return withMeta(
      {
        display_name: data.display_name ?? data.title,
        items: data.items.map(item => ({
          title: item.title ?? '',
          description: item.description ?? item.detail ?? '',
          affectsLabel: item.affectsLabel,
          category: item.category,
          iconVariant: item.iconVariant,
        })),
      } as InsightListData,
      meta,
    );
  }

  if (data.headline != null || data.description != null) {
    return withMeta(
      {
        display_name: data.display_name ?? data.title,
        overline: data.overline,
        headline: data.headline ?? data.title ?? '',
        description: data.description ?? data.body,
      } as NarrativeBlockData,
      meta,
    );
  }

  throw new Error('Unrecognized insight payload shape');
};
